import React, { FC, useCallback, useMemo } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { UnknownUserIcon } from '@modernloop/shared/icons';
import { ListItem, ListItemButton, Stack, Typography } from '@mui/material';

import { MemberSelect_UserInfoFragment } from 'src/generated/mloop-graphql';

import SingleMemberSelect from 'src/entities/Select/MemberSelect/SingleMemberSelect';
import MemberOption from 'src/entities/Select/MemberSelect/SingleMemberSelect/MemberOption';

import useEmployeeId from 'src/hooks/useEmployeeId';

import { EMPTY_UUID } from 'src/constants';

export const ScheduleTaskAssingeeFilterUnassignedItemId = EMPTY_UUID;

interface AssigneeSelectProps {
  getLabel: () => React.ReactNode;
  onChange: (newMember: MemberSelect_UserInfoFragment | null) => void;
}

const AssigneeSelect: FC<AssigneeSelectProps> = ({ getLabel, onChange }) => {
  const { t } = useTranslation();
  const currentEmployeeId = useEmployeeId();
  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: MemberSelect_UserInfoFragment) => {
      if (option.userId === ScheduleTaskAssingeeFilterUnassignedItemId) {
        return (
          <ListItem {...props}>
            <ListItemButton>
              <Stack direction="row" alignItems="center" spacing={1}>
                <UnknownUserIcon />
                <Typography>{t('hiring-dashboard-tasks:tasks.create.unassigned')}</Typography>
              </Stack>
            </ListItemButton>
          </ListItem>
        );
      }

      if (!currentEmployeeId) {
        return null;
      }

      return (
        <ListItem
          {...props}
          data-testid={
            option.employee?.id === currentEmployeeId
              ? 'schedule-task-assignee-current-user-option'
              : 'schedule-task-assignee-option'
          }
        >
          <ListItemButton>
            <MemberOption currentEmployeeId={currentEmployeeId} member={option} />
          </ListItemButton>
        </ListItem>
      );
    },
    [currentEmployeeId, t]
  );

  const additionalOptions = useMemo(() => {
    return [
      {
        userId: ScheduleTaskAssingeeFilterUnassignedItemId,
        userName: t('hiring-dashboard-tasks:tasks.create.unassigned'),
        employee: {
          id: ScheduleTaskAssingeeFilterUnassignedItemId,
          internalProfilePictureUrl: null,
          fullName: t('hiring-dashboard-tasks:tasks.create.unassigned'),
        },
      },
    ];
  }, [t]);

  return (
    <SingleMemberSelect
      dataTestId="schedule-task-assignee-select"
      additionalOptions={additionalOptions}
      renderOption={renderOption}
      onMemberSelect={(newUser) => {
        const selectedUser = newUser;
        if (selectedUser?.employee?.id === ScheduleTaskAssingeeFilterUnassignedItemId) {
          selectedUser.employee.id = null;
        }
        onChange(selectedUser);
      }}
      getLabel={getLabel}
    />
  );
};

export default AssigneeSelect;
